import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, FileDown, Mail } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ModalPortal from "../../components/ModalPortal";

const backend = process.env.REACT_APP_BACKEND_URL;

export default function AdminCustomers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadHistory = (u) => {
    const token = localStorage.getItem("optivize_token");
    fetch(`${backend}/api/admin/customers/${u.id}/payment-history.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : Promise.reject(new Error("download failed")))
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `payment-history-${u.email}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>Customers</h2>
        <button onClick={()=>setShowCreate(true)} className="btn-primary inline-flex items-center gap-2" data-testid="add-customer-btn">
          <UserPlus size={14}/> Add manual customer
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400"/></div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-customers-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr><th className="py-3 px-4 text-left">Name</th><th className="text-left">Email</th><th className="text-left">Role</th><th className="text-left">Company</th><th className="text-left">Phone</th><th className="text-right">Joined</th><th className="text-right pr-4">Actions</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-slate-800" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 text-white">{u.name}</td>
                  <td className="text-slate-300">{u.email}</td>
                  <td><span className={`tag ${u.role==='admin' ? '!text-amber-300 !border-amber-400/30 !bg-amber-400/5' : ''}`}>{u.role}</span></td>
                  <td className="text-slate-400">{u.company || "—"}</td>
                  <td className="text-slate-400">{u.phone || "—"}</td>
                  <td className="text-right text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="text-right pr-4">
                    <button onClick={()=>downloadHistory(u)} title="Download payment history (PDF)" className="text-slate-300 hover:text-white p-1.5" data-testid={`history-${u.id}`}>
                      <FileDown size={16}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateCustomerModal onClose={()=>setShowCreate(false)} onCreated={()=>{setShowCreate(false); load();}}/>}
    </div>
  );
}

function CreateCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", password: "", send_welcome_email: true });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const { data } = await api.post("/admin/customers", payload);
      toast.success(`Customer created${data.generated_password ? ` · password: ${data.generated_password}` : ""}`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="create-customer-modal">
      <form onSubmit={submit} className="card max-w-md w-full p-6">
        <h3 className="text-white font-semibold text-lg mb-3" style={{fontFamily:'Outfit'}}>Add manual customer</h3>
        <div className="space-y-3">
          <div><label className="label">Name</label><input required className="input" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="cc-name"/></div>
          <div><label className="label">Email</label><input required type="email" className="input" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="cc-email"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Phone</label><input className="input" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} data-testid="cc-phone"/></div>
            <div><label className="label">Company</label><input className="input" value={form.company} onChange={e=>setForm({...form, company: e.target.value})} data-testid="cc-company"/></div>
          </div>
          <div><label className="label">Password (leave blank to auto-generate)</label><input className="input" value={form.password} onChange={e=>setForm({...form, password: e.target.value})} data-testid="cc-password"/></div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={form.send_welcome_email} onChange={e=>setForm({...form, send_welcome_email: e.target.checked})} data-testid="cc-send-mail"/>
            Send welcome email with sign-in details
          </label>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-4" data-testid="cc-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="cc-save">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Mail size={14}/>} Create & email
          </button>
        </div>
      </form>
    </div></ModalPortal>
  );
}
